import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveTotal,
	platformDisplayName
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	AnalyticsSentimentByPlatformResponse,
	GetSocialListeningMyBrandAnalyticsSentimentByPlatformService
} from '../../../../../../services/get-social-listening-my-brand-analytics-sentiment-by-platform.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

interface LegendItem {
	label: string;
	color: string;
}

// Sentiment palette from the LOLLY report kit (Figma 13302:100611):
// green positive, muted-grey neutral, red negative.
const SENTIMENT_COLORS = {
	positive: '#2bb673',
	neutral: '#9aa3bb',
	negative: '#e94f6a'
};

@Component({
	standalone: true,
	selector: 'app-sentiment-by-media-type-slide',
	templateUrl: './sentiment-by-media-type-slide.component.html',
	styleUrls: ['./sentiment-by-media-type-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class SentimentByMediaTypeSlideComponent
	implements OnChanges, OnDestroy
{
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();
	private destroy$ = new Subject<void>();
	private cachedSentimentData: AnalyticsSentimentByPlatformResponse | null =
		null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedSentimentData ||
			!this.cachedSentimentData.platforms.some(platform =>
				hasPositiveTotal([
					platform.negative,
					platform.neutral,
					platform.positive
				])
			)
		);
	}

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	chartSectionTitle = 'Sentiment by channel';
	chartData: ChartConfiguration<'bar'>['data'] | null = null;
	chartOptions: ChartConfiguration<'bar'>['options'] | null = null;
	readonly chartType = 'bar' as const;
	legendItems: LegendItem[] = [];
	// Top-right callout — the channel with the highest positive share.
	kpiValue = '';
	kpiLabel = '';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'sentiment_by_media_type',
		null,
		null
	);

	constructor(
		private analyticsService: GetSocialListeningMyBrandAnalyticsSentimentByPlatformService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedSentimentData && this.cacheKey === currentCacheKey) {
			this.applySentimentData(this.cachedSentimentData);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedSentimentData = data;
				this.cacheKey = currentCacheKey;
				this.applySentimentData(data);
			});
	}

	private applySentimentData(
		data: AnalyticsSentimentByPlatformResponse
	): void {
		const rawSlide = this.buildSlide(data);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.updateKpi(data);
		this.slideBuilt.emit(slide);
	}

	// Best positive share across channels -> "74%" + "Positive on Instagram".
	private updateKpi(data: AnalyticsSentimentByPlatformResponse): void {
		let bestShare = -1;
		let bestPlatform = '';
		for (const p of data.platforms) {
			const total = p.positive + p.neutral + p.negative;
			if (total <= 0) continue;
			const share = p.positive / total;
			if (share > bestShare) {
				bestShare = share;
				bestPlatform = platformDisplayName(p.platform);
			}
		}
		if (bestShare >= 0 && bestPlatform) {
			this.kpiValue = `${Math.round(bestShare * 100)}%`;
			this.kpiLabel = `Positive on ${bestPlatform}`;
		} else {
			this.kpiValue = '';
			this.kpiLabel = '';
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(
		data: AnalyticsSentimentByPlatformResponse
	): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		const platforms = data.platforms.map(p =>
			platformDisplayName(p.platform)
		);
		return {
			id: `${prefix}_slide`,
			slideType: 'sentiment_by_media_type',
			title: this.item.title || 'Sentiment by Media Type',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Sentiment by Media Type',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				{
					id: `${prefix}_chart`,
					kind: 'chart',
					visible: true,
					content: {
						// Grouped bars = chart.js 'bar' type with multiple datasets
						// ('grouped_bar' is not a ChartType in the widget contract).
						chartType: 'bar',
						dataSourceKey: 'sentiment_by_media_type_chart',
						seriesMeta: [
							{
								key: 'positive',
								label: 'Positive',
								color: SENTIMENT_COLORS.positive
							},
							{
								key: 'neutral',
								label: 'Neutral',
								color: SENTIMENT_COLORS.neutral
							},
							{
								key: 'negative',
								label: 'Negative',
								color: SENTIMENT_COLORS.negative
							}
						],
						chartJsConfig: {
							type: 'bar',
							data: {
								labels: platforms,
								datasets: [
									{
										label: 'Positive',
										data: data.platforms.map(
											p => p.positive
										),
										backgroundColor:
											SENTIMENT_COLORS.positive,
										borderRadius: 4,
										borderSkipped: false,
										categoryPercentage: 0.7,
										barPercentage: 0.9
									},
									{
										label: 'Neutral',
										data: data.platforms.map(
											p => p.neutral
										),
										backgroundColor:
											SENTIMENT_COLORS.neutral,
										borderRadius: 4,
										borderSkipped: false,
										categoryPercentage: 0.7,
										barPercentage: 0.9
									},
									{
										label: 'Negative',
										data: data.platforms.map(
											p => p.negative
										),
										backgroundColor:
											SENTIMENT_COLORS.negative,
										borderRadius: 4,
										borderSkipped: false,
										categoryPercentage: 0.7,
										barPercentage: 0.9
									}
								]
							},
							options: {
								responsive: true,
								maintainAspectRatio: false,
								scales: {
									x: {
										stacked: false,
										border: { display: false },
										grid: { display: false },
										ticks: {
											color: '#6b7796',
											font: {
												family: 'Gilroy',
												size: 11
											},
											maxRotation: 0,
											autoSkip: false
										}
									},
									y: {
										stacked: false,
										beginAtZero: true,
										border: { display: false },
										grid: {
											color: '#e6eaf2',
											drawTicks: false
										},
										ticks: {
											color: '#6b7796',
											font: {
												family: 'Gilroy',
												size: 11
											},
											padding: 8
										}
									}
								},
								plugins: {
									legend: { display: false },
									tooltip: {
										callbacks: {
											label: (ctx: any) =>
												`${ctx.dataset.label}: ${ctx.parsed.y}`
										}
									}
								}
							}
						}
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		this.dateLabel =
			this.getTitleWidget('date_range', hidden, slide)?.content.text ??
			slide.dateRange?.label ??
			'';
		this.summaryText =
			this.getSummaryWidget(hidden, slide)?.content.text ??
			slide.description ??
			'';
		this.title =
			this.getTitleWidget('title', hidden, slide)?.content.text ??
			slide.title;
		this.titleColor =
			this.getTitleWidget('title', hidden, slide)?.content.color ??
			'#2872e1';
		const chartWidget =
			(slide.widgets.find(
				widget => widget.kind === 'chart' && !hidden.has(widget.id)
			) as ChartWidget | undefined) ?? null;
		if (chartWidget) {
			const config = chartWidget.content
				.chartJsConfig as unknown as ChartConfiguration<'bar'>;
			this.chartData = config?.data ?? null;
			this.chartOptions = config?.options ?? null;
			this.legendItems = (chartWidget.content.seriesMeta ?? []).map(
				meta => ({
					label: meta.label,
					color: meta.color ?? ''
				})
			);
		} else {
			this.chartData = null;
			this.chartOptions = null;
			this.legendItems = [];
		}

		// Channels along one axis, a sentiment per series, in the colours the
		// legend above names.
		const datasets = this.chartData?.datasets ?? [];
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			datasets.length
				? sectionChartSource(this.item.id, {
						labels: (this.chartData?.labels ?? []) as string[],
						series: datasets.map(dataset => ({
							key: String(dataset.label ?? '').toLowerCase(),
							label: String(dataset.label ?? ''),
							values: dataset.data as number[],
							color: dataset.backgroundColor as string
						})),
						labelColumn: 'Channel',
						seriesLegendShown: true
				  })
				: null
		);
	}

	private getTitleWidget(
		kind: TitleWidget['kind'],
		hidden: Set<string>,
		slide: PreviewSlide
	): TitleWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === kind && !hidden.has(widget.id)
		) as TitleWidget | undefined;
	}

	private getSummaryWidget(
		hidden: Set<string>,
		slide: PreviewSlide
	): SummaryTextWidget | undefined {
		return slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;
	}
}
